import React from 'react';
import axios from 'axios';
import * as PhosphorIcons from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import TimeBreakdownTable from './tables/TimeBreakdownTable';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import TableHeader from '../../components/generic/TableHeader';
import TimeSpentChart from './charts/TimeSpentChart';
import QuestionsAttemptedChart from './charts/QuestionsAttemptedChart';
import AccuracyChart from './charts/AccuracyChart';
import QualityOfTimeChart from './charts/QualityOfTimeChart';
import TimeJourneyTable from './tables/TimeJourneyTable';
import TimeJourneyChart from './charts/TimeJourneyChart';
import ComparativeTimeBreakdownTable from './tables/ComparativeTimeBreakdownTable';
import ComparativeHourWiseTable from './tables/ComparativeHourWiseTable';
import ComparativeHourwiseChart from './charts/ComparativeHourwiseChart';
import TestAnalysisLoader from '../../components/loaders/TestAnalysisLoader';

export default function TimeAnalysis({ isComparative, comparativeData, packId, testId, totalTime }) {
  const [subjectMeta, setSubjectMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const subjectwiseTabs = [
    {
      title: 'Time Spent',
      icon: 'TimerIcon',
      color: 'var(--q3-neutral-default)',
    },
    {
      title: 'Questions Attempted',
      icon: 'QuestionIcon',
      color: 'var(--q3-neutral-default)',
    },
    {
      title: 'Accuracy',
      icon: 'TargetIcon',
      color: 'var(--q3-neutral-default)',
    },
  ];
  const [selectedSubjectwiseTab, setSelectedSubjectwiseTab] = useState(subjectwiseTabs[0]);
  const [selectedQualitySubject, setSelectedQualitySubject] = useState(null);
  const [data, setData] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const hourWiseTabs = [
    {
      title: 'Overall',
      icon: 'ChecksIcon',
      color: 'var(--q3-neutral-default)',
    },
    {
      title: 'Correct',
      icon: 'CheckIcon',
      color: 'var(--q3-green-normal)',
    },
    {
      title: 'Incorrect',
      icon: 'XIcon',
      color: 'var(--q3-danger-normal)',
    },
  ];
  const [selectedHourwiseTab, setSelectedHourwiseTab] = useState(hourWiseTabs[0]);
  const [selectedGraphicalHourwiseTab, setSelectedGraphicalHourwiseTab] = useState(hourWiseTabs[1]);

  const handleSubjectwiseTabChange = (tab) => {
    setSelectedSubjectwiseTab(tab);
  };

  const handleHourwiseTabChange = (tab) => {
    setSelectedHourwiseTab(tab);
  };

  const handleGraphicalHourwiseTabChange = (tab) => {
    setSelectedGraphicalHourwiseTab(tab);
  };

  const renderIcon = (icon, color) => {
    if (icon && PhosphorIcons[icon]) {
      return React.createElement(PhosphorIcons[icon], {
        size: 24,
        color: color ? color : 'var(--q3-neutral-default)',
      });
    }
    return null;
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const res = await axios.get(`/api/test/analysis/v2/${packId}/${testId}/timeaccuracy`);
        const response = res?.data?.data;
        setSubjectMeta(response?.subjectMeta);
        setSubjectMeta(response?.subjectMeta);
        setSelectedQualitySubject(Object.values(response?.subjectMeta)[0]);
        setData(response);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }

    if (!isComparative) {
      fetchData();
    }
  }, [packId, testId]);

  useEffect(() => {
    if (!isComparative || !comparativeData) return undefined;
    let subjectMeta = structuredClone(comparativeData?.subjectMeta);
    if (Object.keys(subjectMeta).length > 2) {
      const overall = { Overall: subjectMeta['Overall'] };
      delete subjectMeta['Overall'];
      subjectMeta = { ...overall, ...subjectMeta };
    } else {
      delete subjectMeta['Overall'];
    }
    setSubjectMeta(subjectMeta);
    setSelectedSubject(Object.values(subjectMeta)[0]);
  }, [isComparative, comparativeData]);

  const handleQualitySubjectChange = (subject) => {
    setSelectedQualitySubject(subject);
  };

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
  };

  if (isLoading) return <TestAnalysisLoader />;

  if (isComparative && comparativeData && subjectMeta)
    return (
      <div className="space-y-8">
        <TableHeader headerTitle={'Time Breakdown'} headerTitleClass={'border-b-0'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab?._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} className="" />}
                  <p className="mb-0">{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <ComparativeTimeBreakdownTable comparativeData={comparativeData} selectedSubject={selectedSubject} />
        </TableHeader>
        {comparativeData?.comparativeData?.hour_wise_breakdown?.length > 0 && (
          <div className="space-y-8">
            <TableHeader headerTitle={'Hour Wise Breakdown'} headerTitleClass={'border-b-0'}>
              <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
                <TabsContainer color={selectedHourwiseTab?.color}>
                  {hourWiseTabs?.map((tab, index) => (
                    <Tab
                      key={index}
                      isActive={selectedHourwiseTab?.title === tab?.title}
                      color={
                        selectedHourwiseTab?.title === tab?.title
                          ? selectedHourwiseTab?.color
                          : 'var(--q3-neutral-light)'
                      }
                      tabColor={tab?.color}
                      bgColor="var(--q3-surface-dim)"
                      onClick={() => handleHourwiseTabChange(tab)}
                    >
                      {tab.icon && renderIcon(tab.icon, tab.color)}
                      <p className="mb-0">{tab?.title}</p>
                    </Tab>
                  ))}
                </TabsContainer>
              </div>
              <ComparativeHourWiseTable comparativeData={comparativeData} selectedHourWiseTab={selectedHourwiseTab} />
            </TableHeader>
            <TableHeader headerTitle={'Graphical Hour-Wise Breakdown'} headerTitleClass={'border-b-0'}>
              <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
                <TabsContainer color={selectedGraphicalHourwiseTab?.color}>
                  {hourWiseTabs
                    ?.filter((tab) => tab?.title !== 'Overall')
                    ?.map((tab, index) => (
                      <Tab
                        key={index}
                        isActive={selectedGraphicalHourwiseTab?.title === tab?.title}
                        color={
                          selectedGraphicalHourwiseTab?.title === tab?.title
                            ? selectedGraphicalHourwiseTab?.color
                            : 'var(--q3-neutral-light)'
                        }
                        tabColor={tab?.color}
                        bgColor="var(--q3-surface-dim)"
                        onClick={() => handleGraphicalHourwiseTabChange(tab)}
                      >
                        {tab.icon && renderIcon(tab.icon, tab.color)}
                        <p className="mb-0">{tab?.title}</p>
                      </Tab>
                    ))}
                </TabsContainer>
              </div>
              <ComparativeHourwiseChart
                comparativeData={comparativeData}
                selectedHourWiseTab={selectedGraphicalHourwiseTab}
              />
            </TableHeader>
          </div>
        )}
      </div>
    );

  return (
    <div className="space-y-8">
      <TableHeader headerTitle="Time Breakdown">
        <TimeBreakdownTable subjectMeta={subjectMeta} data={data?.timeAndAccuracy} />
      </TableHeader>
      <TableHeader headerTitle={'Subject-wise'} headerTitleClass={'border-b-0'}>
        <div className="text-[var(--q3-neutral-default)] text-lg font-bold rounded-none">
          <div className="border border-[var(--q3-stroke-light)] border-y-0">
            <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
              <TabsContainer color={selectedSubjectwiseTab?.color}>
                {subjectwiseTabs?.map((tab) => (
                  <Tab
                    key={tab._id}
                    isActive={selectedSubjectwiseTab?.title === tab.title}
                    color={
                      selectedSubjectwiseTab?.title === tab.title
                        ? selectedSubjectwiseTab?.color
                        : 'var(--q3-neutral-light)'
                    }
                    tabColor={tab.color}
                    bgColor="var(--q3-surface-dim)"
                    onClick={() => handleSubjectwiseTabChange(tab)}
                  >
                    {tab.icon && renderIcon(tab.icon)}
                    <p className="mb-0">{tab.title}</p>
                  </Tab>
                ))}
              </TabsContainer>
            </div>

            <div className="flex flex-row bg-[var(--q3-surface-dim)] gap-6 py-4 px-6">
              {subjectMeta &&
                Object.values(subjectMeta)
                  .filter((item) => item?.title !== 'Overall')
                  ?.map((subject) => (
                    <div className="flex flex-row items-center gap-2">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: subject?.color }} />
                      <p className="text-[var(--q3-neutral-default)] text-sm font-medium">{subject?.title}</p>
                    </div>
                  ))}
            </div>
          </div>
          {selectedSubjectwiseTab?.title === 'Time Spent' ? (
            <TimeSpentChart timeData={data?.timeAndAccuracy} />
          ) : selectedSubjectwiseTab?.title === 'Questions Attempted' ? (
            <QuestionsAttemptedChart questionsData={data?.timeAndAccuracy} />
          ) : selectedSubjectwiseTab?.title === 'Accuracy' ? (
            <AccuracyChart accuracyData={data?.timeAndAccuracy} />
          ) : null}
        </div>
      </TableHeader>
      <TableHeader headerTitle="Quality of Time Spent" headerTitleClass={'border-b-0'}>
        <div className="text-[var(--q3-neutral-default)] text-lg font-bold border border-y-0 border-[var(--q3-stroke-light)] rounded-none">
          {subjectMeta && Object.values(subjectMeta)?.length > 2 && (
            <div className="w-full overflow-x-auto">
              <TabsContainer color={selectedQualitySubject?.color}>
                {Object.values(subjectMeta)?.map((tab) => (
                  <Tab
                    key={tab._id}
                    isActive={selectedQualitySubject?.title === tab.title}
                    color={
                      selectedQualitySubject?.title === tab.title
                        ? selectedQualitySubject?.color
                        : 'var(--q3-neutral-light)'
                    }
                    tabColor={tab.color}
                    bgColor="var(--q3-surface-dim)"
                    onClick={() => handleQualitySubjectChange(tab)}
                  >
                    {tab.icon && <img src={tab.icon} alt={tab.title} className="" />}
                    <p className="mb-0">{tab.title}</p>
                  </Tab>
                ))}
              </TabsContainer>
            </div>
          )}
        </div>
        <QualityOfTimeChart qualityOfTime={data?.qualityOfTime} selectedSubject={selectedQualitySubject} />
      </TableHeader>
      <TableHeader headerTitle={'Time Journey'}>
        <TimeJourneyTable timeJourney={data?.timeJourney} />
      </TableHeader>
      <TableHeader
        headerTitle={`Graphical View of Attempts Over ${totalTime >= 60 ? `${totalTime / 60} Hour(s)` : `${totalTime} Minutes`}`}
      >
        <TimeJourneyChart timeJourney={data?.timeJourney} />
      </TableHeader>
    </div>
  );
}
